
/** The permissions a call needs, in the order the plugin exposes them. */
const CALL_PERMISSION_NAMES = ['CAMERA', 'RECORD_AUDIO', 'MODIFY_AUDIO_SETTINGS', 'ACCESS_NETWORK_STATE'];

export interface ICallPermissionResult {
    /** Whether camera and microphone may be used. */
    granted: boolean;
    /** Whether the OS dialog was put in front of the user to get there. */
    prompted: boolean;
}

const getCordovaCallPermissions = (): any => {
    const cordova = (globalThis as any).cordova;
    return cordova && cordova.plugins && cordova.plugins.permissions ? cordova.plugins.permissions : undefined;
}

const getNeededCordovaPermissions = (permissions: any): string[] => {
    return CALL_PERMISSION_NAMES
        .map((name) => permissions[name])
        .filter((permission) => typeof permission === 'string' && permission.length > 0);
}

/**
 * The plugin's checkPermission takes exactly one permission. Handing it an array makes the
 * native side stringify the whole array into a single unknown permission name, which the
 * system always reports as denied - so every permission gets its own check.
 */
const hasCordovaPermission = (permissions: any, permission: string): Promise<boolean> => {
    return new Promise((resolve, reject) => {
        permissions.checkPermission(
            permission,
            (status: any) => resolve(!!(status && status.hasPermission)),
            (error: any) => reject(error)
        );
    });
}

const getBrowserMediaDevices = (): MediaDevices | undefined => {
    const nav = globalThis.navigator as Navigator | undefined;
    return nav && nav.mediaDevices ? nav.mediaDevices : undefined;
}

const queryBrowserPermissionState = async (name: string): Promise<string> => {
    const nav = globalThis.navigator as Navigator | undefined;
    if (!nav || !nav.permissions || typeof nav.permissions.query !== 'function') {
        return 'unsupported';
    }

    try {
        const status = await nav.permissions.query({name} as any);
        return status.state || 'unsupported';
    } catch (_) {
        return 'unsupported';
    }
}

const hasLabeledBrowserDevices = async () => {
    const mediaDevices = getBrowserMediaDevices();
    if (!mediaDevices || typeof mediaDevices.enumerateDevices !== 'function') {
        return false;
    }

    try {
        const devices = await mediaDevices.enumerateDevices();
        const hasNamedVideoInput = devices.some((device) => device.kind === 'videoinput' && !!device.label);
        const hasNamedAudioInput = devices.some((device) => device.kind === 'audioinput' && !!device.label);
        return hasNamedVideoInput && hasNamedAudioInput;
    } catch (_) {
        return false;
    }
}

const stopTracksSafely = (stream: MediaStream) => {
    try {
        stream.getTracks().forEach((track) => track.stop());
    } catch (_) {
        // ignore
    }
}

const hasBrowserCallPermission = async () => {
    const mediaDevices = getBrowserMediaDevices();
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
        return false;
    }

    const [cameraState, microphoneState] = await Promise.all([
        queryBrowserPermissionState('camera'),
        queryBrowserPermissionState('microphone')
    ]);

    if (cameraState === 'granted' && microphoneState === 'granted') {
        return true;
    }

    if (cameraState === 'denied' || microphoneState === 'denied') {
        return false;
    }

    return hasLabeledBrowserDevices();
}

const requestBrowserCallPermission = async () => {
    const mediaDevices = getBrowserMediaDevices();
    if (!mediaDevices || typeof mediaDevices.getUserMedia !== 'function') {
        throw new Error('Camera/Mic access is not available in this browser context.');
    }

    if (typeof globalThis.isSecureContext === 'boolean' && !globalThis.isSecureContext) {
        throw new Error('Camera/Mic access requires a secure context (https or localhost).');
    }

    try {
        const stream = await mediaDevices.getUserMedia({audio: true, video: true});
        stopTracksSafely(stream);
        return true;
    } catch (error) {
        const typedError = error as any;
        const errorName = typedError && typeof typedError === 'object' ? typedError.name : '';
        if (errorName === 'AbortError' || errorName === 'NotAllowedError' || errorName === 'PermissionDeniedError' || errorName === 'SecurityError') {
            return false;
        }
        throw typedError;
    }
}

export const hasCallPermission = (): Promise<boolean> => {
    const permissions = getCordovaCallPermissions();

    if (!permissions) {
        return hasBrowserCallPermission();
    }

    const needed = getNeededCordovaPermissions(permissions);
    return Promise.all(needed.map((permission) => hasCordovaPermission(permissions, permission)))
        .then((results) => results.every((granted) => granted));
}

export const requestCallPermission = (): Promise<boolean> => {
    const permissions = getCordovaCallPermissions();

    if (!permissions) {
        return requestBrowserCallPermission();
    }

    const needed = getNeededCordovaPermissions(permissions);
    return new Promise<boolean>((resolve, reject) => {
        permissions.requestPermissions(
            needed,
            (status: any) => resolve(!!(status && status.hasPermission)),
            (error: any) => reject(error)
        );
    });
}

const resolveCallPermission = async (): Promise<ICallPermissionResult> => {
    if (await hasCallPermission()) {
        return {granted: true, prompted: false};
    }

    const granted = await requestCallPermission();
    return {granted, prompted: true};
}

/**
 * The request everybody currently waiting on the OS dialog shares.
 *
 * The Android plugin keeps a single pending callback, and Android itself refuses a second
 * concurrent request outright, so two overlapping asks - placing a call just as another
 * one comes in - would leave one of them waiting on a result that is never delivered.
 */
let callPermissionInFlight: Promise<ICallPermissionResult> | undefined;

/**
 * Gets call media permission, prompting only when it is not already granted.
 *
 * `prompted` matters to the caller: on Android the OS dialog backgrounds the app, so
 * anything time-sensitive - a media session, a signalling socket - has to be started
 * after this resolves rather than before.
 */
export const ensureCallPermission = (): Promise<ICallPermissionResult> => {
    if (!callPermissionInFlight) {
        const clear = () => {
            callPermissionInFlight = undefined;
        };

        callPermissionInFlight = resolveCallPermission().then(
            (result) => {
                clear();
                return result;
            },
            (error) => {
                clear();
                throw error;
            }
        );
    }

    return callPermissionInFlight;
}



// WEBPACK FOOTER //
// ./src/services/permissions/permission.ts